import { PayloadAction } from '@reduxjs/toolkit';
import { SagaIterator } from 'redux-saga';
import { call, put, takeEvery, takeLatest } from 'redux-saga/effects';

import { BetaFeatureRequest } from '@/Interfaces/I_betaFeature';

import { searchAccounts } from '@/Axios/api/accountsManagement';
import { getAllUserGroups } from '@/Axios/api/userGroup';

import {
  createBetaFeature,
  deleteBetaFeature,
  getAllBetaFeatures,
  updateBetaFeature
} from '../../axios/api/betaFeature';
import {
  betaFeatureSearchUserGroups,
  betaFeatureSearchUsers,
  deleteBetaFeature as deleteBetaFeatureAction,
  getBetaFeaturesData,
  postCreateBetaFeatue,
  putUpdateBetaFeatue,
  setBetaFeatures,
  setBetaFeatureSearchedUserGroups,
  setBetaFeatureSearchedUsers,
  setBetaFeaturesError
} from '../slices/BetaFeaturesSlice';

function* handleFetchBetaFeatures(): SagaIterator {
  try {
    const response = yield call(getAllBetaFeatures);
    yield put(setBetaFeatures(response.data));
  } catch (e) {
    yield put(setBetaFeaturesError((e as Error).message || 'Failed to fetch beta features'));
  }
}

function* handleCreateBetaFeature(action: PayloadAction<BetaFeatureRequest>): SagaIterator {
  try {
    yield call(createBetaFeature, action.payload);
    yield put(getBetaFeaturesData());
  } catch (e) {
    yield put(setBetaFeaturesError((e as Error).message || 'Failed to create beta feature'));
  }
}

function* handleUpdateBetaFeature(
  action: PayloadAction<{ id: number; data: BetaFeatureRequest }>,
): SagaIterator {
  try {
    const { id, data } = action.payload;
    yield call(updateBetaFeature, id, data);
    yield put(getBetaFeaturesData());
  } catch (e) {
    yield put(setBetaFeaturesError((e as Error).message || 'Failed to update beta feature'));
  }
}

function* handleDeleteBetaFeature(action: PayloadAction<number>): SagaIterator {
  try {
    yield call(deleteBetaFeature, action.payload);
    yield put(getBetaFeaturesData());
  } catch (e) {
    yield put(setBetaFeaturesError((e as Error).message || 'Failed to delete beta feature'));
  }
}

function* handleSearchUsers(action: PayloadAction<string>): SagaIterator {
  try {
    const response = yield call(searchAccounts, { search: action.payload, page: 0, size: 10 });
    yield put(setBetaFeatureSearchedUsers(response.data.data));
  } catch (e) {
    yield put(setBetaFeaturesError((e as Error).message || 'Failed to search users'));
  }
}
function* handleSearchUserGroups(action: PayloadAction<string>): SagaIterator {
  try {
    const response = yield call(getAllUserGroups, { search: action.payload, page: 0, size: 10 });
    yield put(setBetaFeatureSearchedUserGroups(response.data.data));
  } catch (e) {
    yield put(setBetaFeaturesError((e as Error).message || 'Failed to search user groups'));
  }
}

export function* watchFetchBetaFeatures() {
  yield takeLatest(getBetaFeaturesData.type, handleFetchBetaFeatures);
}
export function* watchCreateBetaFeature() {
  yield takeEvery(postCreateBetaFeatue.type, handleCreateBetaFeature);
}
export function* watchUpdateBetaFeature() {
  yield takeEvery(putUpdateBetaFeatue.type, handleUpdateBetaFeature);
}
export function* watchDeleteBetaFeature() {
  yield takeLatest(deleteBetaFeatureAction.type, handleDeleteBetaFeature);
}
export function* watchBetaFeatureSearchUser() {
  yield takeLatest(betaFeatureSearchUsers.type, handleSearchUsers);
}
export function* watchBetaFeatureSearchUserGroups() {
  yield takeLatest(betaFeatureSearchUserGroups.type, handleSearchUserGroups);
}
